import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ListState, StatusBadge } from "@/components/common";
import { LinkDialog } from "@/components/LinkDialog";
import ExportDialog from "@/pages/admin/ExportDialog";
import { RosterOnlyDialog } from "@/components/RosterOnlyDialog";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDateDMY } from "@/lib/format";
import { Users, Search, KeyRound, Download, UserPlus, Trash2 } from "lucide-react";
import { DeleteEmployeesDialog } from "@/components/DeleteEmployeesDialog";
import { useZones } from "@/components/StateZoneFields";
import { INDIAN_STATES } from "@/lib/states";
import { toast } from "sonner";

const FORM_STATUSES = ["not_started", "draft", "submitted", "change_requested", "reopened", "roster_only"];

export default function Employees() {
  const { actingOrgId, isManagerOnly } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [formStatus, setFormStatus] = useState("");
  const [deptId, setDeptId] = useState("");
  const [locId, setLocId] = useState("");
  const [desigId, setDesigId] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [zoneId, setZoneId] = useState("");
  const zones = useZones();
  const [roOpen, setRoOpen] = useState(false);
  const [link, setLink] = useState("");
  const [selected, setSelected] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [toDelete, setToDelete] = useState([]);

  const { data: depts = [] } = useQuery({ queryKey: ["depts", actingOrgId], queryFn: async () => (await api.get("/org/masters/departments")).data });
  const { data: locs = [] } = useQuery({ queryKey: ["locs", actingOrgId], queryFn: async () => (await api.get("/org/masters/locations")).data });
  const { data: desigs = [] } = useQuery({ queryKey: ["desigs", actingOrgId], queryFn: async () => (await api.get("/org/masters/designations")).data });

  const { data: employees = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: ["employees", actingOrgId, search, formStatus, deptId, locId, desigId, stateCode, zoneId],
    queryFn: async () => (await api.get("/org/employees", {
      params: { search, form_status: formStatus, department_id: deptId, location_id: locId, designation_id: desigId, state_code: stateCode, zone_id: zoneId },
    })).data,
  });

  const resetLink = useMutation({
    mutationFn: async (empId) => (await api.post(`/org/employees/${empId}/reset-link`)).data,
    onSuccess: (d) => { setLink(d.link); toast.success(d.sent_status === "sent" ? "Reset link emailed" : "Reset link generated"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader title="Employees" description={isManagerOnly ? "Employees in your locations and departments (read-only)" : "Search and filter your organisation's employees"}
        actions={
          <div className="flex flex-wrap gap-2">
            {!isManagerOnly && <Button variant="outline" onClick={() => setRoOpen(true)} data-testid="open-roster-only-btn"><UserPlus className="mr-2 h-4 w-4" /> Add roster-only</Button>}
            {!isManagerOnly && selected.length > 0 && (
              <Button variant="destructive" onClick={() => setToDelete(employees.filter((e) => selected.includes(e.id)))} data-testid="bulk-delete-employees-btn">
                <Trash2 className="mr-2 h-4 w-4" /> Delete ({selected.length})
              </Button>
            )}
            {!isManagerOnly && (
              <Button onClick={() => setExportOpen(true)} data-testid="open-export-btn">
                <Download className="mr-2 h-4 w-4" /> Export{selected.length ? ` (${selected.length})` : ""}
              </Button>
            )}
          </div>
        } />
      <RosterOnlyDialog open={roOpen} onClose={() => setRoOpen(false)} onDone={refetch} />

      <Card className="mb-5 flex flex-wrap items-center gap-3 p-4">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search name, email, code" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="employee-search" />
        </div>
        <Select value={formStatus || "all"} onValueChange={(v) => setFormStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[170px]" data-testid="filter-form-status"><SelectValue placeholder="Form status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {FORM_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={deptId || "all"} onValueChange={(v) => setDeptId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="filter-department"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={locId || "all"} onValueChange={(v) => setLocId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="filter-location"><SelectValue placeholder="Location" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All locations</SelectItem>
            {locs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={desigId || "all"} onValueChange={(v) => setDesigId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="filter-designation"><SelectValue placeholder="Designation" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All designations</SelectItem>
            {desigs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stateCode || "all"} onValueChange={(v) => setStateCode(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="filter-state"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All states</SelectItem>
            <SelectItem value="none">Not assigned</SelectItem>
            {INDIAN_STATES.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={zoneId || "all"} onValueChange={(v) => setZoneId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="filter-zone"><SelectValue placeholder="Zone" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All zones</SelectItem>
            <SelectItem value="none">Not assigned</SelectItem>
            {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      <ListState isLoading={isLoading} isError={isError} error={error} onRetry={refetch}
        isEmpty={employees.length === 0} emptyTitle="No employees found" emptyHint="Invite employees to get started" icon={Users}>
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {!isManagerOnly && <TableHead className="w-8"></TableHead>}
                <TableHead>Name</TableHead><TableHead>Code</TableHead><TableHead>Department</TableHead>
                <TableHead>Location</TableHead><TableHead>State / Zone</TableHead><TableHead>Joined</TableHead><TableHead>Form</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((e) => (
                <TableRow key={e.id} data-testid={`employee-row-${e.email}`} className="cursor-pointer"
                  onClick={() => navigate(`/org/employees/${e.id}`)}>
                  {!isManagerOnly && <TableCell onClick={(ev) => ev.stopPropagation()}>
                    <Checkbox checked={selected.includes(e.id)} data-testid={`select-${e.email}`}
                      onCheckedChange={(v) => setSelected(v ? [...selected, e.id] : selected.filter((x) => x !== e.id))} />
                  </TableCell>}
                  <TableCell><p className="text-sm font-medium text-slate-800">{e.name}</p><p className="text-xs text-slate-400">{e.email}</p></TableCell>
                  <TableCell className="text-sm">{e.employee_code || "-"}</TableCell>
                  <TableCell className="text-sm">{e.department_name || "-"}</TableCell>
                  <TableCell className="text-sm">{e.location_name || "-"}</TableCell>
                  <TableCell className="text-sm" data-testid={`emp-state-zone-${e.email}`}>{e.state_name || "-"}<p className="text-xs text-slate-400">{e.zone_name || "No zone"}</p></TableCell>
                  <TableCell className="text-sm">{formatDateDMY(e.date_of_joining)}</TableCell>
                  <TableCell><StatusBadge status={e.form_status} /></TableCell>
                  <TableCell onClick={(ev) => ev.stopPropagation()}>
                    {e.user_id && !isManagerOnly && (
                      <Button size="sm" variant="ghost" title="Generate reset link" data-testid={`reset-link-${e.email}`}
                        onClick={() => resetLink.mutate(e.id)}>
                        <KeyRound className="h-4 w-4" />
                      </Button>
                    )}
                    {!isManagerOnly && (
                      <Button size="sm" variant="ghost" title="Delete employee" className="text-rose-600 hover:text-rose-700" data-testid={`delete-employee-${e.email}`}
                        onClick={() => setToDelete([e])}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </ListState>

      <LinkDialog open={!!link} onClose={() => { setLink(""); refetch(); }} title="Password reset link"
        note="This one-time link is valid for 24 hours and forces the user to set a new password." link={link} />

      <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)}
        employeeIds={selected} formStatusFilter={formStatus} />
      <DeleteEmployeesDialog employees={toDelete} onClose={() => setToDelete([])}
        onDeleted={() => { const gone = toDelete.map((e) => e.id); setSelected(selected.filter((x) => !gone.includes(x))); setToDelete([]); refetch(); }} />
    </div>
  );
}
